import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { PublicHolidayListResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormData = {
  name: string;
  holidays: {
    month: string;
    day: number;
    name: string;
  }[];
};

const monthOptions = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const HolidayBox = styled.div`
  display: grid;
  padding: 16px 32px;
  margin-bottom: 16px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  margin-left: -32px;
  width: calc(100% + 64px);
`;

const RemoveButton = styled.button`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
    font-weight: ${theme.typography.button.fontWeight};
    color: ${theme.colors.error};
    background: ${theme.colors.errorLight};
  `}
  grid-column: span 2;
  text-align: center;
  border-radius: 4px;
  border: solid 1px transparent;
  padding: 0 11px;
  height: 27px;
  margin-top: -6px;
  align-items: center;
  font-size: 11px;
  line-height: 15px;
  transition: border-color ease-out 0.2s;
  &:hover {
    border: solid 1px;
  }
`;

const AddButtonHolder = styled.div`
  margin-top: 24px;
  button {
    width: 100%;
  }
`;

const HolidaysSection = styled.div`
  grid-column: span 2;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

interface Props {
  list?: PublicHolidayListResource;
  onClose: () => void;
}

const PublicHolidayListFormModal: FC<Props> = ({ list, onClose }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: list?.name || '',
      holidays: list?.holidays?.map((h) => ({
        month: String(h.month),
        day: h.day,
        name: h.name || '',
      })) || [{ month: '', day: 1, name: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'holidays',
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (formData) => {
      try {
        const data = {
          name: formData.name,
          holidays: formData.holidays.map((h) => ({
            month: Number(h.month),
            day: Number(h.day),
            name: h.name || null,
          })),
        };

        if (list) {
          await api.put<typeof data, PublicHolidayListResource>(
            `/v1/payments/freelancers/holidays/public-holiday-lists/${list.id}`,
            data,
          );
        } else {
          await api.post<typeof data, PublicHolidayListResource>(
            '/v1/payments/freelancers/holidays/public-holiday-lists',
            data,
          );
        }

        await queryClient.invalidateQueries(['public-holiday-lists'], {
          exact: false,
        });

        onClose();
        alerts.success(
          `Public holiday list was successfully ${list ? 'updated' : 'created'}`,
        );
      } catch (error) {
        handleFormErrors<FormData>(error, setError, alerts, true);
      }
    })(e);
  };

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const addHoliday = () => {
    append({ month: '', day: 1, name: '' });
  };

  return (
    <Modal>
      <SidePanel
        heading={`${list ? 'Edit' : 'Add'} Public Holiday List`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormData>
                name="name"
                control={control}
                label="List Name"
                placeholder="Add name"
              />
              <HolidaysSection>
                <SectionHeader>
                  <Text $type="caption">Public Holidays</Text>
                </SectionHeader>
                {fields.map((field, index) => (
                  <HolidayBox key={field.id}>
                    <FormLayout>
                      <div>
                        <FormField<FormData>
                          name={`holidays.${index}.month`}
                          control={control}
                          type="select"
                          options={monthOptions}
                          label="Month"
                        />
                      </div>
                      <div>
                        <FormField<FormData, number>
                          name={`holidays.${index}.day`}
                          control={control}
                          type="integer"
                          label="Day"
                          placeholder="1-31"
                        />
                      </div>
                      <FormField<FormData>
                        name={`holidays.${index}.name`}
                        control={control}
                        label="Holiday Name"
                        placeholder="e.g., Christmas Day"
                        optional
                      />
                      {fields.length > 1 && (
                        <RemoveButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(index);
                          }}
                        >
                          Remove Holiday
                        </RemoveButton>
                      )}
                    </FormLayout>
                  </HolidayBox>
                ))}
                <AddButtonHolder>
                  <Button type="button" $type="colorized" onClick={addHoliday}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2V14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 8H14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Add more
                  </Button>
                </AddButtonHolder>
              </HolidaysSection>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {list ? 'Update' : 'Add'}
            </Button>
            <Button color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default PublicHolidayListFormModal;
